import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label
} from "./components/ui";
import { useLanguage } from "./LanguageContext";

const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_URL;
  if (env && env.trim().length > 0) {
    const trimmed = env.trim().replace(/\/+$/, "");
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }
  if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
    return "http://127.0.0.1:8000";
  }
  return "/api";
};
const API_URL = resolveApiBase();

const BASE_SETTINGS_COPY = {
  pageTitle: "General Settings",
  pageSubtitle: "These values apply to all users.",
  alerts: {
    errorTitle: "Something went wrong",
    successTitle: "Success",
    readOnlyTitle: "Read only",
    readOnlyDescription: "Only administrators can update settings."
  },
  card: {
    title: "SellerFlash bearer token",
    description: "Used for SellerFlash invoice generation flows."
  },
  form: {
    label: "SellerFlash Bearer token",
    placeholder: "Enter bearer token",
    save: "Save settings",
    saving: "Saving..."
  },
  messages: {
    loadError: "Failed to load settings",
    saveError: "Failed to save settings",
    saveSuccess: "Settings saved successfully."
  }
};

const SETTINGS_TRANSLATIONS = {
  en: BASE_SETTINGS_COPY,
  tr: {
    ...BASE_SETTINGS_COPY,
    pageTitle: "Genel ayarlar",
    pageSubtitle: "Bu değerler tüm kullanıcılar için geçerlidir.",
    alerts: {
      errorTitle: "Bir sorun oluştu",
      successTitle: "Başarılı",
      readOnlyTitle: "Salt okunur",
      readOnlyDescription: "Yalnızca yöneticiler ayarları güncelleyebilir."
    },
    card: {
      title: "SellerFlash bearer belirteci",
      description: "SellerFlash fatura oluşturma süreçlerinde kullanılır."
    },
    form: {
      label: "SellerFlash Bearer belirteci",
      placeholder: "Bearer belirtecini girin",
      save: "Ayarları kaydet",
      saving: "Kaydediliyor..."
    },
    messages: {
      loadError: "Ayarlar yüklenemedi",
      saveError: "Ayarlar kaydedilemedi",
      saveSuccess: "Ayarlar başarıyla kaydedildi."
    }
  }
};

const Settings = ({ token, role }) => {
  const [bearerToken, setBearerToken] = useState("");
  const [amazonCardLast4, setAmazonCardLast4] = useState("4253");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { language } = useLanguage();
  const translations = SETTINGS_TRANSLATIONS[language] || SETTINGS_TRANSLATIONS.en;

  const canEdit = role === "admin";

  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    const fetchSettings = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${API_URL}/settings`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });
        setBearerToken(response.data?.sellerflash_bearer_token || "");
        setAmazonCardLast4((response.data?.amazon_card_last4 || "4253").toString());
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError(err.response?.data?.detail || translations.messages.loadError);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
    return () => controller.abort();
  }, [token, translations.messages.loadError]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canEdit || !token) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await axios.put(
        `${API_URL}/settings`,
        { sellerflash_bearer_token: bearerToken, amazon_card_last4: amazonCardLast4 },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSuccess(translations.messages.saveSuccess);
    } catch (err) {
      setError(err.response?.data?.detail || translations.messages.saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{translations.pageTitle}</h1>
        <p className="text-sm text-muted-foreground">{translations.pageSubtitle}</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>{translations.alerts.errorTitle}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert>
          <AlertTitle>{translations.alerts.successTitle}</AlertTitle>
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <Card className="border-border/60 bg-card/70">
        <CardHeader>
          <CardTitle>{translations.card.title}</CardTitle>
          <CardDescription>{translations.card.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {!canEdit && (
            <Alert variant="destructive" className="mb-4 border-amber-500/40 bg-amber-500/10 text-amber-800">
              <AlertTitle>{translations.alerts.readOnlyTitle}</AlertTitle>
              <AlertDescription>{translations.alerts.readOnlyDescription}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sellerflash_bearer_token">{translations.form.label}</Label>
              <Input
                id="sellerflash_bearer_token"
                value={bearerToken}
                onChange={(event) => setBearerToken(event.target.value)}
                placeholder={translations.form.placeholder}
                disabled={loading || !canEdit}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amazon_card_last4">Amazon card last 4 digits</Label>
              <Input
                id="amazon_card_last4"
                value={amazonCardLast4}
                onChange={(event) => setAmazonCardLast4(event.target.value)}
                placeholder="4253"
                disabled={loading || !canEdit}
              />
              <p className="text-xs text-muted-foreground">
                Used by Amazon Business verification to find the transaction line (e.g. “MasterCard ending in 4253”).
              </p>
            </div>
            <Button type="submit" disabled={!canEdit || saving} className="gap-2">
              {saving ? translations.form.saving : translations.form.save}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
